import { useIdentityContext, useUserData } from '@flik/shared';
import { useReactQuery } from '@flik/shared/api';
import { KebabMenu, useTheme } from '@flik/shared/components';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NavBar from '../components/NavBar';

// Email validation helper
const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const OrganizationManagementPage: React.FC = () => {
  const { provideTailwind } = useTheme();
  const navigate = useNavigate();
  const { currentOrganization, availableOrganizations } = useIdentityContext();
  const { whoamiData } = useUserData();
  const apiClient = useReactQuery();
  const [userRoles, setUserRoles] = useState<{
    [userId: string]: string | null;
  }>({});
  const fetchedUserIdsRef = useRef<Set<string>>(new Set());
  const [searchInput, setSearchInput] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [orderBy, setOrderBy] = useState<'FirstName' | 'LastName'>('FirstName');
  const [activeTab, setActiveTab] = useState<'Users' | 'Invitations'>('Users');
  const [reverseSearch, setReverseSearch] = useState(false);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);

  // Invite user state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [isInviting, setIsInviting] = useState(false);

  // Role change confirmation state
  const [showDemoteConfirmation, setShowDemoteConfirmation] = useState(false);
  const [pendingRoleChange, setPendingRoleChange] = useState<{
    userId: string;
    newRole: 'StandardUser' | 'PowerUser';
  } | null>(null);

  // Role change error state
  const [roleChangeError, setRoleChangeError] = useState<string | null>(null);
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get organization name
  const organization = useMemo(() => {
    if (!currentOrganization || !availableOrganizations) return null;
    return availableOrganizations.find(
      (org: any) => org.organization_id === currentOrganization,
    );
  }, [currentOrganization, availableOrganizations]);

  const organizationName = organization?.organization_name || 'Organization';

  // Check if user is PowerUser
  const isPowerUser = whoamiData?.role === 'PowerUser';

  // Reset to Users tab if user is not PowerUser and currently on Invitations tab
  useEffect(() => {
    if (!isPowerUser && activeTab === 'Invitations') {
      setActiveTab('Users');
    }
  }, [isPowerUser, activeTab]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  // Fetch users with filtering and pagination
  const usersQuery = apiClient.useQuery('get', '/v1/list-users', {
    params: {
      query: {
        ...(reverseSearch ? { last: 50 } : { first: 50 }),
        match: searchFilter || null,
        order_by: orderBy,
      },
    },
    enabled: !!currentOrganization,
    staleTime: 30 * 1000, // 30 seconds
    cacheTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch pending invitations (only AwaitingResponse)
  const pendingInvitationsQuery = apiClient.useQuery(
    'get',
    '/v1/list-organization-user-invitations',
    {
      params: {
        query: {
          first: 50,
          status: 'AwaitingResponse' as const,
        },
      },
      enabled: !!currentOrganization,
      staleTime: 30 * 1000, // 30 seconds
      cacheTime: 5 * 60 * 1000, // 5 minutes
    },
  );

  // Invite user mutation
  const inviteUserMutation = apiClient.useMutation(
    'post',
    '/v1/invite-user-to-organization',
  );

  // Set user role mutation
  const setUserRoleMutation = apiClient.useMutation(
    'post',
    '/v1/set-user-role',
  );

  const users = usersQuery.data?.result?.items || [];
  const isLoading = usersQuery.isLoading;
  const isError = usersQuery.isError;
  const pendingInvitations = pendingInvitationsQuery.data?.result?.items || [];
  const isLoadingInvitations = pendingInvitationsQuery.isLoading;

  // Fetch user roles using whois API
  const whoisMutation = apiClient.useMutation('post', '/v1/whois');

  useEffect(() => {
    if (users.length > 0) {
      // Extract identity IDs from users
      const identityIds = users.map((user) => user.identity_id).filter(Boolean);

      // Only fetch roles for users we haven't fetched yet
      const identityIdsToFetch = identityIds.filter(
        (id: string) => !fetchedUserIdsRef.current.has(id),
      );

      if (identityIdsToFetch.length > 0) {
        // Mark these as being fetched
        identityIdsToFetch.forEach((id) => fetchedUserIdsRef.current.add(id));

        whoisMutation
          .mutateAsync({
            body: {
              identityIds: identityIdsToFetch,
            },
          })
          .then((response) => {
            if (response?.users) {
              const newRoles: { [userId: string]: string | null } = {};
              response.users.forEach((whoisUser) => {
                if (whoisUser && whoisUser.identity) {
                  newRoles[whoisUser.identity.id] = whoisUser.role || null;
                }
              });
              setUserRoles((prev) => ({ ...prev, ...newRoles }));
            }
          })
          .catch((error) => {
            console.error('Failed to fetch user roles:', error);
            // Remove from fetched set on error so we can retry
            identityIdsToFetch.forEach((id) =>
              fetchedUserIdsRef.current.delete(id),
            );
          });
      }
    }
  }, [users, whoisMutation]);

  // Handle search input with debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchFilter(searchInput);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Get user display name
  const getUserDisplayName = (user: any) => {
    if (user.first_name || user.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }
    return user.nickname || 'Unknown User';
  };

  // Handle sort option selection
  const handleSortOption = (
    field: 'FirstName' | 'LastName',
    ascending: boolean,
  ) => {
    setOrderBy(field);
    setReverseSearch(!ascending);
    setSortDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  const sortDropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sortDropdownRef.current &&
        !sortDropdownRef.current.contains(event.target as Node)
      ) {
        setSortDropdownOpen(false);
      }
    };

    if (sortDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [sortDropdownOpen]);

  // Handle invite user
  const handleInviteUser = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedEmail = inviteEmail.trim().toLowerCase();
    if (!trimmedEmail) {
      setInviteError('Please enter an email address');
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setInviteError('Please enter a valid email address');
      return;
    }

    setInviteError(null);
    setInviteSuccess(false);
    setIsInviting(true);

    try {
      await inviteUserMutation.mutateAsync({
        body: {
          userEmail: trimmedEmail,
        },
      });

      setInviteSuccess(true);
      setInviteEmail('');

      // Refetch invitations after a short delay
      setTimeout(() => {
        pendingInvitationsQuery.refetch();
      }, 500);
    } catch (error) {
      console.error('Failed to invite user:', error);
      setInviteError(
        error instanceof Error
          ? error.message
          : 'Failed to invite user. Please try again.',
      );
    } finally {
      setIsInviting(false);
    }
  };

  // Format date for display
  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'Unknown date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get current user's identity ID
  const currentUserIdentityId = whoamiData?.identity?.id;

  // Handle role change
  const handleRoleChange = (
    userId: string,
    newRole: 'StandardUser' | 'PowerUser',
  ) => {
    // Check if user is demoting themselves from PowerUser
    const isSelfDemotion =
      currentUserIdentityId === userId &&
      newRole === 'StandardUser' &&
      userRoles[userId] === 'PowerUser';

    if (isSelfDemotion) {
      // Show confirmation modal
      setPendingRoleChange({ userId, newRole });
      setShowDemoteConfirmation(true);
    } else {
      // Proceed directly with role change
      performRoleChange(userId, newRole);
    }
  };

  // Perform the actual role change
  const performRoleChange = async (
    userId: string,
    newRole: 'StandardUser' | 'PowerUser',
  ) => {
    try {
      // Clear any previous errors
      setRoleChangeError(null);

      await setUserRoleMutation.mutateAsync({
        body: {
          identityId: userId,
          role: newRole,
        },
      });

      // Update the userRoles state immediately
      setUserRoles((prev) => ({ ...prev, [userId]: newRole }));

      // Refetch users to get updated data
      usersQuery.refetch();
    } catch (error) {
      console.error('Failed to change user role:', error);

      // Extract error message
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to change user role. Please try again.';

      // Show error message
      setRoleChangeError(errorMessage);

      // Clear any existing timeout
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }

      // Auto-hide error after 3 seconds
      errorTimeoutRef.current = setTimeout(() => {
        setRoleChangeError(null);
        errorTimeoutRef.current = null;
      }, 3000);
    }
  };

  // Handle confirmation of self-demotion
  const handleConfirmDemotion = () => {
    if (pendingRoleChange) {
      performRoleChange(pendingRoleChange.userId, pendingRoleChange.newRole);
      setShowDemoteConfirmation(false);
      setPendingRoleChange(null);
    }
  };

  // Handle cancellation of self-demotion
  const handleCancelDemotion = () => {
    setShowDemoteConfirmation(false);
    setPendingRoleChange(null);
  };

  return (
    <div className={`w-full min-h-screen ${provideTailwind('bg-primary')}`}>
      <NavBar />

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Organization Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className={`text-sm ${provideTailwind(
              'text-tertiary',
            )} hover:${provideTailwind('text-primary')} transition-colors mb-4`}
          >
            ← Back to dashboard
          </button>
          <h1
            className={`text-4xl font-light ${provideTailwind('text-primary')}`}
          >
            {organizationName}
          </h1>
        </div>

        {/* Combined Users and Invitations Section */}
        <div className="mt-8">
          {/* Role Change Error Message */}
          {roleChangeError && (
            <div
              className={`mb-6 p-4 rounded-lg border-2 ${provideTailwind(
                'bg-secondary',
              )} border-red-500 ${provideTailwind(
                'text-red-500',
              )} flex items-center justify-between`}
            >
              <div className="flex items-center space-x-2">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="font-medium">{roleChangeError}</span>
              </div>
              <button
                onClick={() => setRoleChangeError(null)}
                className={`ml-4 text-red-500 hover:opacity-80 transition-opacity`}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          )}

          {/* Tabs */}
          <div
            className={`flex items-center space-x-1 mb-6 border-b ${provideTailwind(
              'border-tertiary',
            )}`}
          >
            <button
              onClick={() => setActiveTab('Users')}
              className={`px-6 py-3 text-base font-medium transition-colors border-b-2 ${
                activeTab === 'Users'
                  ? `${provideTailwind('text-primary')} ${provideTailwind(
                      'border-primary',
                    )}`
                  : `${provideTailwind(
                      'text-tertiary',
                    )} border-transparent hover:${provideTailwind(
                      'text-primary',
                    )}`
              }`}
            >
              Users
            </button>
            {isPowerUser && (
              <button
                onClick={() => setActiveTab('Invitations')}
                className={`px-6 py-3 text-base font-medium transition-colors border-b-2 ${
                  activeTab === 'Invitations'
                    ? `${provideTailwind('text-primary')} ${provideTailwind(
                        'border-primary',
                      )}`
                    : `${provideTailwind(
                        'text-tertiary',
                      )} border-transparent hover:${provideTailwind(
                        'text-primary',
                      )}`
                }`}
              >
                Invitations
              </button>
            )}
          </div>

          {/* Users Tab Content */}
          {activeTab === 'Users' && (
            <>
              {/* Filter Section */}
              <div className="mb-6 flex items-center gap-4">
                {/* Sort Button with Dropdown */}
                <div className="relative" ref={sortDropdownRef}>
                  <button
                    onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                    className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${provideTailwind(
                      'bg-secondary',
                    )} ${provideTailwind('text-primary')} ${provideTailwind(
                      'border-secondary',
                    )} border-2 hover:border-opacity-60 hover:${provideTailwind(
                      'border-primary',
                    )} focus:outline-none focus:ring-2 focus:${provideTailwind(
                      'ring-primary',
                    )} ${
                      sortDropdownOpen
                        ? `${provideTailwind(
                            'border-primary',
                          )} ${provideTailwind('bg-primary')} ${provideTailwind(
                            'text-primary-button',
                          )}`
                        : ''
                    }`}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"
                      />
                    </svg>
                    <span className="text-sm font-semibold uppercase tracking-wider">
                      Sort
                    </span>
                    <svg
                      className={`w-4 h-4 transition-transform ${
                        sortDropdownOpen ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {/* Dropdown Menu */}
                  {sortDropdownOpen && (
                    <div
                      className={`absolute top-full left-0 mt-2 w-56 rounded-lg shadow-lg border-2 ${provideTailwind(
                        'bg-secondary',
                      )} ${provideTailwind(
                        'border-secondary',
                      )} z-50 overflow-hidden`}
                    >
                      <div className="py-1">
                        <button
                          onClick={() => handleSortOption('FirstName', true)}
                          className={`w-full px-4 py-2.5 text-left text-sm transition-colors flex items-center justify-between ${
                            orderBy === 'FirstName' && !reverseSearch
                              ? `${provideTailwind(
                                  'bg-primary',
                                )} ${provideTailwind('text-primary-button')}`
                              : `${provideTailwind(
                                  'text-primary',
                                )} hover:${provideTailwind('bg-secondary')}`
                          }`}
                        >
                          <span>First name (ascending)</span>
                          {orderBy === 'FirstName' && !reverseSearch && (
                            <svg
                              className="w-4 h-4"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </button>
                        <button
                          onClick={() => handleSortOption('FirstName', false)}
                          className={`w-full px-4 py-2.5 text-left text-sm transition-colors flex items-center justify-between ${
                            orderBy === 'FirstName' && reverseSearch
                              ? `${provideTailwind(
                                  'bg-primary',
                                )} ${provideTailwind('text-primary-button')}`
                              : `${provideTailwind(
                                  'text-primary',
                                )} hover:${provideTailwind('bg-secondary')}`
                          }`}
                        >
                          <span>First name (descending)</span>
                          {orderBy === 'FirstName' && reverseSearch && (
                            <svg
                              className="w-4 h-4"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </button>
                        <button
                          onClick={() => handleSortOption('LastName', true)}
                          className={`w-full px-4 py-2.5 text-left text-sm transition-colors flex items-center justify-between ${
                            orderBy === 'LastName' && !reverseSearch
                              ? `${provideTailwind(
                                  'bg-primary',
                                )} ${provideTailwind('text-primary-button')}`
                              : `${provideTailwind(
                                  'text-primary',
                                )} hover:${provideTailwind('bg-secondary')}`
                          }`}
                        >
                          <span>Last name (ascending)</span>
                          {orderBy === 'LastName' && !reverseSearch && (
                            <svg
                              className="w-4 h-4"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </button>
                        <button
                          onClick={() => handleSortOption('LastName', false)}
                          className={`w-full px-4 py-2.5 text-left text-sm transition-colors flex items-center justify-between ${
                            orderBy === 'LastName' && reverseSearch
                              ? `${provideTailwind(
                                  'bg-primary',
                                )} ${provideTailwind('text-primary-button')}`
                              : `${provideTailwind(
                                  'text-primary',
                                )} hover:${provideTailwind('bg-secondary')}`
                          }`}
                        >
                          <span>Last name (descending)</span>
                          {orderBy === 'LastName' && reverseSearch && (
                            <svg
                              className="w-4 h-4"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Search Input */}
                <div className="flex-1 relative group">
                  <div
                    className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${provideTailwind(
                      'text-tertiary',
                    )} group-focus-within:${provideTailwind(
                      'text-primary',
                    )} transition-colors`}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className={`w-full pl-11 pr-4 py-3 rounded-lg ${provideTailwind(
                      'bg-secondary',
                    )} ${provideTailwind('text-primary')} ${provideTailwind(
                      'border-secondary',
                    )} border-2 focus:outline-none focus:border-opacity-60 focus:${provideTailwind(
                      'border-primary',
                    )} transition-all placeholder:${provideTailwind(
                      'text-tertiary',
                    )} placeholder:opacity-60`}
                  />
                </div>
              </div>

              {/* Users List */}
              {isLoading ? (
                <div
                  className={`text-center py-12 ${provideTailwind(
                    'text-secondary',
                  )}`}
                >
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-current"></div>
                  <p className="mt-4">Loading users...</p>
                </div>
              ) : isError ? (
                <div
                  className={`text-center py-12 ${provideTailwind(
                    'text-red-500',
                  )}`}
                >
                  <p>Error loading users. Please try again.</p>
                </div>
              ) : users.length === 0 ? (
                <div
                  className={`text-center py-12 ${provideTailwind(
                    'text-secondary',
                  )}`}
                >
                  <p>
                    {searchFilter
                      ? 'No users found matching your search.'
                      : 'No users found.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {users.map((user) => (
                    <div
                      key={user.id}
                      className={`${provideTailwind(
                        'bg-secondary/30',
                      )} rounded-lg p-4 border-0 hover:${provideTailwind(
                        'bg-secondary/50',
                      )} transition-all duration-200`}
                    >
                      <div className="flex items-center space-x-4 w-full">
                        {/* Profile Picture or Initials */}
                        <div className="w-12 h-12 rounded-full flex items-center justify-center bg-blue-600 text-white font-medium text-lg flex-shrink-0">
                          {user.profile_picture_url ? (
                            <img
                              src={user.profile_picture_url}
                              alt={getUserDisplayName(user)}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                          ) : (
                            <span>
                              {(user.first_name?.[0] || '') +
                                (user.last_name?.[0] || '') ||
                                user.nickname?.[0] ||
                                'U'}
                            </span>
                          )}
                        </div>

                        {/* User Info */}
                        <div className="flex-1 min-w-0 text-left">
                          <h3
                            className={`text-base font-medium ${provideTailwind(
                              'text-primary',
                            )}`}
                          >
                            {`${user.first_name || ''} ${
                              user.last_name || ''
                            }`.trim()}
                            {user.nickname && ` (${user.nickname})`}
                          </h3>
                          <p
                            className={`text-sm ${provideTailwind(
                              'text-tertiary',
                            )}`}
                          >
                            {user.email || ''}
                          </p>
                        </div>

                        {/* Power User Badge */}
                        {userRoles[user.identity_id] === 'PowerUser' && (
                          <div
                            className="px-4 py-1.5 bg-white hover:bg-gray-50 text-black rounded-full transition-all font-medium text-sm flex items-center shadow-sm relative flex-shrink-0"
                            style={{
                              background:
                                'linear-gradient(white, white) padding-box, linear-gradient(to right, #ec4899, #8b5cf6) border-box',
                              border: '1px solid transparent',
                            }}
                          >
                            Power
                          </div>
                        )}

                        {/* Kebab Menu (only for PowerUsers) */}
                        {isPowerUser && user.identity_id && (
                          <div className="flex-shrink-0">
                            <KebabMenu
                              actions={[
                                userRoles[user.identity_id] === 'PowerUser'
                                  ? {
                                      id: 'demote',
                                      label: 'Demote to standard user',
                                      onClick: () =>
                                        handleRoleChange(
                                          user.identity_id,
                                          'StandardUser',
                                        ),
                                    }
                                  : {
                                      id: 'promote',
                                      label: 'Promote to power user',
                                      onClick: () =>
                                        handleRoleChange(
                                          user.identity_id,
                                          'PowerUser',
                                        ),
                                    },
                              ]}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Invitations Tab Content */}
          {activeTab === 'Invitations' && (
            <>
              {/* Invite User Form */}
              <form
                onSubmit={handleInviteUser}
                className={inviteError || inviteSuccess ? 'mb-10' : 'mb-6'}
              >
                <div className="flex items-center space-x-4">
                  <div className="flex-1 relative">
                    <input
                      type="email"
                      placeholder="Enter email to invite"
                      value={inviteEmail}
                      onChange={(e) => {
                        setInviteEmail(e.target.value);
                        setInviteError(null);
                        setInviteSuccess(false);
                      }}
                      className={`w-full px-4 py-2 rounded-lg ${provideTailwind(
                        'bg-secondary',
                      )} ${provideTailwind('text-primary')} ${provideTailwind(
                        'border-secondary',
                      )} border focus:outline-none focus:ring-2 focus:${provideTailwind(
                        'ring-primary',
                      )}`}
                    />
                    {(inviteError || inviteSuccess) && (
                      <p
                        className={`absolute -bottom-6 left-0 text-sm ${
                          inviteError
                            ? provideTailwind('text-red-500')
                            : provideTailwind('text-green-500')
                        }`}
                      >
                        {inviteError || 'Invitation sent successfully!'}
                      </p>
                    )}
                  </div>
                  <button
                    type="submit"
                    disabled={isInviting}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${provideTailwind(
                      'bg-primary-button',
                    )} ${provideTailwind(
                      'text-primary-button',
                    )} ${provideTailwind(
                      'hover:bg-primary-button',
                    )} disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap`}
                  >
                    {isInviting ? 'Sending...' : 'Invite'}
                  </button>
                </div>
              </form>

              {/* Invitations List */}
              {isLoadingInvitations ? (
                <div
                  className={`text-center py-8 ${provideTailwind(
                    'text-secondary',
                  )}`}
                >
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-current"></div>
                  <p className="mt-2">Loading invitations...</p>
                </div>
              ) : pendingInvitations.length === 0 ? (
                <div
                  className={`text-center py-6 ${provideTailwind(
                    'text-tertiary',
                  )}`}
                >
                  <p className="text-sm">No pending invitations</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {pendingInvitations.map((invitation: any) => (
                    <div
                      key={invitation.identity_id}
                      className={`${provideTailwind(
                        'bg-secondary/30',
                      )} rounded-lg p-4 border-0`}
                    >
                      <div>
                        <p
                          className={`text-base font-medium ${provideTailwind(
                            'text-primary',
                          )}`}
                        >
                          {invitation.user_email}
                        </p>
                        <p
                          className={`text-sm mt-1 ${provideTailwind(
                            'text-tertiary',
                          )}`}
                        >
                          Invited on {formatDate(invitation.created_on)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Self-Demotion Confirmation Modal */}
      {showDemoteConfirmation && (
        <div
          className={`fixed inset-0 ${provideTailwind(
            'bg-primary',
          )} bg-opacity-75 flex items-center justify-center z-[9999]`}
        >
          <div
            className={`${provideTailwind(
              'bg-primary',
            )} rounded-lg p-6 max-w-md w-full mx-4 shadow-xl border-2 ${provideTailwind(
              'border-secondary',
            )}`}
          >
            <h3
              className={`text-lg font-semibold mb-4 ${provideTailwind(
                'text-primary',
              )}`}
            >
              Confirm demotion
            </h3>
            <div className={`mb-6 ${provideTailwind('text-primary')}`}>
              <p className="mb-3">
                You are about to demote yourself from Power User to Standard
                User.
              </p>
              <p
                className={`text-sm ${provideTailwind(
                  'text-tertiary',
                )} font-medium`}
              >
                ⚠️ Warning: You will lose access to Power User features,
                including the ability to manage users and invitations. You will
                need another Power User to restore your privileges.
              </p>
            </div>
            <div className="flex space-x-3 justify-end">
              <button
                onClick={handleCancelDemotion}
                className={`px-4 py-2 text-sm font-medium ${provideTailwind(
                  'text-secondary',
                )} ${provideTailwind(
                  'hover:bg-secondary',
                )} rounded-md transition-colors`}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDemotion}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationManagementPage;
